/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router';
import { X } from 'lucide-react';
import type { ShopperSearch } from '@/scapi';
import { useNavigate } from '@/hooks/use-navigate';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { applyRefinesToSearchParams } from '@/components/eataly-plp-filters/lib/refine-url';
import {
    hasActivePriceFacet,
    hasPriceFacetRefinement,
    replacePriceRefine,
} from '@/components/eataly-plp-filters/lib/price-facet';
import { useMediaQuery } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import { SvgClose } from '@/components/icons-eataly';
import EatalyPlpPriceFilterPanel from './price-filter-panel';

const LG_UP = '(min-width: 1024px)';

type EatalyPlpPriceFilterProps = {
    /** When false (product listings), the sticky Prezzo control is hidden. */
    enabled?: boolean;
    refinements?: ShopperSearch.schemas['ProductSearchResult']['refinements'];
    refine?: string[];
    /** Shared button class with Ordina / Filtra pills. */
    triggerClassName?: string;
    testIdPrefix?: string;
};

export default function EatalyPlpPriceFilter({
    enabled = false,
    refinements,
    refine = [],
    triggerClassName = 'h-10 min-h-10 gap-1 px-4 py-2 cursor-pointer',
    testIdPrefix = 'plp',
}: EatalyPlpPriceFilterProps): ReactElement | null {
    const { t } = useTranslation('categoryPlp');
    const isLgUp = useMediaQuery(LG_UP);
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const [open, setOpen] = useState(false);

    const effectiveRefines = useMemo(() => {
        if (navigation.location) {
            return new URLSearchParams(navigation.location.search).getAll('refine');
        }
        return refine;
    }, [navigation.location, refine]);

    const showPriceFilter = enabled && hasPriceFacetRefinement(refinements);
    const isActive = hasActivePriceFacet(effectiveRefines);

    const priceLabel = t('stickyBar.priceLabel');
    const triggerAriaLabel = isActive
        ? t('stickyBar.priceButtonLabelWithSelection', { label: priceLabel })
        : priceLabel;

    const applyPrice = useCallback(
        (rangeString: string | null) => {
            const current = new URLSearchParams(location.search).getAll('refine');
            const nextRefines = replacePriceRefine(current, rangeString);
            const params = new URLSearchParams(location.search);
            applyRefinesToSearchParams(params, nextRefines);
            const serialized = params.toString();
            void navigate({
                pathname: location.pathname,
                search: serialized ? `?${serialized}` : '',
            });
        },
        [location.pathname, location.search, navigate]
    );

    const handleOpenChange = useCallback((nextOpen: boolean) => {
        setOpen(nextOpen);
    }, []);

    if (!showPriceFilter) {
        return null;
    }

    const triggerButton = (
        <Button
            data-testid={`${testIdPrefix}-sticky-bar-price-trigger`}
            hierarchy="primary"
            mode="dark"
            size="small"
            className={triggerClassName}
            aria-label={triggerAriaLabel}
            aria-pressed={open || isActive}
            aria-expanded={open}
            aria-haspopup="dialog"
            onClick={isLgUp ? undefined : () => setOpen(true)}>
            <span>{priceLabel}</span>
            {isActive ? (
                <span className="body-copy-m" aria-hidden>
                    1
                </span>
            ) : null}
        </Button>
    );

    if (isLgUp) {
        return (
            <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
                <PopoverContent
                    data-testid={`${testIdPrefix}-price-filter-popover`}
                    side="bottom"
                    align="end"
                    sideOffset={8}
                    className={cn(
                        'relative w-[min(100vw-2rem,401px)] rounded-lg border border-sand-90 bg-neutral-white p-6 pt-12 shadow-md',
                        'text-neutral-10'
                    )}
                    onCloseAutoFocus={(event) => event.preventDefault()}>
                    <Button
                        type="button"
                        hierarchy="tertiary"
                        className="absolute top-2 right-2"
                        iconOnly
                        aria-label={t('priceFilter.closeAriaLabel')}
                        onClick={() => setOpen(false)}>
                        <SvgClose className="size-6" aria-hidden />
                    </Button>
                    <EatalyPlpPriceFilterPanel
                        refinements={refinements}
                        refine={effectiveRefines}
                        open={open}
                        onApply={applyPrice}
                        onClose={() => setOpen(false)}
                    />
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <>
            {triggerButton}
            <Drawer direction="bottom" open={open} onOpenChange={handleOpenChange} shouldScaleBackground={false}>
                <DrawerContent
                    data-testid={`${testIdPrefix}-price-filter-drawer`}
                    className={cn(
                        'bg-neutral-white border-sand-90',
                        'data-[vaul-drawer-direction=bottom]:max-h-[90vh]'
                    )}
                    showDivider={false}>
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
                        <div className="mb-2 flex shrink-0 items-start justify-end">
                            <DrawerTitle className="sr-only">{priceLabel}</DrawerTitle>
                            <DrawerClose asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="size-10 shrink-0 rounded-full border border-transparent bg-neutral-white p-2 hover:border-neutral-80"
                                    aria-label={t('priceFilter.closeAriaLabel')}>
                                    <X className="size-6 text-neutral-10" aria-hidden />
                                </Button>
                            </DrawerClose>
                        </div>
                        <DrawerDescription className="sr-only">{t('priceFilter.description')}</DrawerDescription>
                        <EatalyPlpPriceFilterPanel
                            refinements={refinements}
                            refine={effectiveRefines}
                            open={open}
                            onApply={applyPrice}
                            onClose={() => setOpen(false)}
                        />
                    </div>
                </DrawerContent>
            </Drawer>
        </>
    );
}
